import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  ChevronDownIcon,
  ChevronUpIcon,
  OptionKey,
  RawButton,
} from '@common-fe/common-fe';
import _ from 'lodash';

import { IPermissionGroup } from './role.types';


interface CheckboxItemProps {
  id?: string;
  checked?: boolean;
  onChangeChecked?: (value: boolean) => void;
  children?: React.ReactNode;
}
const CheckboxItem: React.FC<CheckboxItemProps> = ({ id, children, checked, onChangeChecked }) => {
  return (
    <Box pad={{ horizontal: 'xs', vertical: 'xxs' }}>
      <Checkbox name={id} testId="role" checked={checked} onChange={onChangeChecked}>{children}</Checkbox>
    </Box>
  );
};

interface CheckboxGroupProps extends IPermissionGroup {
  name: string;
  defaultExpanded?: boolean;
  isSelectedMode?: boolean;
  searchString?: string;
  values: OptionKey[];
  defaultValues?: OptionKey[];
  onChangeValues: (values: OptionKey[]) => void;
}

const CheckboxDropdownGroup: React.FC<CheckboxGroupProps> = ({
  onChangeValues,
  defaultExpanded,
  isSelectedMode,
  options,
  defaultValues,
  values,
  name,
  searchString = '',
  id
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded || false);
  const [isSelectedAll, setSelectedAll] = useState(false);

  const handleToggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);
  
  const currentSelectedOptions = useMemo(() => {
    const filteredList =  options.filter((option) => values.includes(option.key));
    
  
    return filteredList;
  }, [options, values]);
  const handleChange = useCallback((key: OptionKey) => () => {  
    setSelectedAll(false);
    if(values.includes(key)) {
      
      onChangeValues(values.filter((value) => value !== key));
    } else {
      onChangeValues([...values, key]);
    }
  }, [onChangeValues, values]);  

  const isIntermediate = useMemo(() => {
    if(isSelectedAll ) {
      return false;
    }
    const allSelected = options.length === currentSelectedOptions.length;
    if(allSelected && !isSelectedAll) {
      setSelectedAll(true);
    }
    return !allSelected && !!currentSelectedOptions.length; 

  }, [currentSelectedOptions.length, isSelectedAll, options]);
  

  const handleSelectAll = useCallback(() => {
    const newValue = !isSelectedAll;
    setSelectedAll(newValue);
    const currentKeys = options.map((item) => item.key);
    if(newValue) {
      onChangeValues(_.uniq([...values,...currentKeys]));
    } else {
      const filteredValues= values.filter((item) => !currentKeys.includes(item as string));
      onChangeValues(filteredValues);
    }
  }, [onChangeValues, options, isSelectedAll, values]);
  
  const filteredOptions = useMemo(() => {
    if(isSelectedMode) {
      return currentSelectedOptions;
    }
    return options;
  }, [currentSelectedOptions, isSelectedMode, options]);
  const sortedOptions = useMemo(() => {
    return _.sortBy(filteredOptions, (item) => defaultValues?.includes(item.key) ? 0 :  1);
  }, [defaultValues, filteredOptions]);

  useEffect(() => {
    if(values.length === 0 && isSelectedAll) {
      setSelectedAll(false);
    }
  }, [isSelectedAll, values.length]);

  const filteredGroups = useMemo(() => {
    const currentSearch =searchString.trim().toLowerCase(); 
    if(!currentSearch){
      return sortedOptions;
    }
   
    return sortedOptions.filter((item) => item.name.toLocaleLowerCase().includes(currentSearch));
  }, [searchString, sortedOptions]);
  if(filteredOptions.length === 0) {
    return null;
  }
  return (
    <Box data-testid={`permission_group_${id}`}>
      <Box direction="row" align="center">
        <RawButton data-testid={`checkbox_expand_${id}`} onClick={handleToggleExpanded}>
          {expanded ? (
            <ChevronUpIcon color="iconPrimary"></ChevronUpIcon>
          ) : (
            <ChevronDownIcon color="iconPrimary"></ChevronDownIcon>
          )}
        </RawButton>
        <Box pad={{ horizontal: 'xs', vertical: 'xxs' }}>
     
          <Checkbox
            name={id}
            testId={`checkbox-group_${id}`}
            manualIndeterminate={isIntermediate}
            checked={isSelectedAll}
            onChange={handleSelectAll}>
            {name}
          </Checkbox>
 
        </Box>
      </Box>
      {expanded && (
        <Box margin={{ left: '30px' }}>
          <Box direction="column" margin={{ left: 'l' }}>
            {filteredGroups.map((option) => (
              <CheckboxItem 
                id={option.key}
                
                key={option.key}
                checked={values.includes(option.key)}
                onChangeChecked={handleChange(option.key)}
              >
                {option.name}
              </CheckboxItem>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
export default React.memo(CheckboxDropdownGroup);
